import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerInfoSelectors from '@makemydeal/dr-dash-store/selectors/offerInfoSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as baseDealActionCreators from '@makemydeal/dr-dash-store/actions/baseDealActionCreators';
import { dealLockHooks } from '../hooks';
import type { FooterActionsType } from '@interstate/components/FooterActions';
import { Typography } from '@interstate/components/Typography';
import { Modal } from '@interstate/components/Modal';

import { ErrorModalDialogContent } from '../common/ErrorModalDialogContent';
import { useValidationGuard } from '../hooks/useValidationGuard';

export type UnsavedChangesDialogProps = {
    onHide(): void;
    onUpdateSuccess(): void;
    onDraftSuccess(): void;
    /** Disarms the beforeunload guard so discarding a draft quote neither auto-saves nor prompts. */
    onLeaveWithoutSaving(): void;
};

export const UnsavedChangesDialog = ({
    onHide,
    onUpdateSuccess,
    onDraftSuccess,
    onLeaveWithoutSaving
}: UnsavedChangesDialogProps) => {
    const dispatch = useDispatch();
    const guard = useValidationGuard('save', { skipValidationKeys: ['vehicle'] });

    const isDraftBeingSavedState = useSelector(offerInfoSelectors.getIsDraftBeingSaved);
    const hasDraftBeenSavedState = useSelector(offerInfoSelectors.getHasDraftBeenSaved);
    const isOfferBeingSavedState = useSelector(offerInfoSelectors.getIsOfferBeingSaved);
    const hasOfferBeenSavedState = useSelector(offerInfoSelectors.getHasBeenSaved);
    const pendingNavigation = useSelector(baseDealSelectors.getPendingNavigation);
    // A deal that was never persisted has no deal exchange record yet, so it cannot be saved as a draft (US2063641).
    const isDealXgRecordMissed = useSelector(offerReduxSelectors.getIsDealXgRecordMissed);
    const isLocked = dealLockHooks.useIsDealLocked();

    const [draftSaveInProgress, setDraftSaveInProgress] = useState(false);
    const [draftSaveFailed, setDraftSaveFailed] = useState(false);
    const [offerSaveInProgress, setOfferSaveInProgress] = useState(false);
    const [offerSaveFailed, setOfferSaveFailed] = useState(false);
    const inProgress = draftSaveInProgress || offerSaveInProgress;

    const handleSaveDraft = useCallback(() => {
        dispatch(offerActionCreators.saveOffer({ isDraft: true }));
        setDraftSaveInProgress(true);
    }, [dispatch]);

    const handleUpdate = useCallback(
        guard.wrap(() => {
            dispatch(offerActionCreators.saveOffer());
            setOfferSaveInProgress(true);
        }),
        [guard.wrap, dispatch]
    );

    useEffect(() => {
        if (draftSaveInProgress) {
            if (isDraftBeingSavedState === false && hasDraftBeenSavedState === false) {
                setDraftSaveInProgress(false);
                setDraftSaveFailed(true);
                setOfferSaveFailed(false);
            } else if (isDraftBeingSavedState === false && hasDraftBeenSavedState === true) {
                onDraftSuccess();
                onHide();
                // Execute pending navigation after successful save
                if (pendingNavigation) {
                    dispatch(baseDealActionCreators.clearPendingNavigation());
                    window.location.assign(pendingNavigation);
                }
            }
        }
    }, [hasDraftBeenSavedState, isDraftBeingSavedState, onHide, draftSaveInProgress, onDraftSuccess, pendingNavigation, dispatch]);
    useEffect(() => {
        if (offerSaveInProgress) {
            if (isOfferBeingSavedState === false && hasOfferBeenSavedState === false) {
                setOfferSaveInProgress(false);
                setOfferSaveFailed(true);
                setDraftSaveFailed(false);
            } else if (isOfferBeingSavedState === false && hasOfferBeenSavedState === true) {
                onUpdateSuccess();
                onHide();
                // Execute pending navigation after successful save
                if (pendingNavigation) {
                    dispatch(baseDealActionCreators.clearPendingNavigation());
                    window.location.assign(pendingNavigation);
                }
            }
        }
    }, [hasOfferBeenSavedState, isOfferBeingSavedState, onHide, offerSaveInProgress, onUpdateSuccess, pendingNavigation, dispatch]);

    const handleCancel = useCallback(() => {
        if (pendingNavigation) {
            dispatch(baseDealActionCreators.clearPendingNavigation());
        }
        onHide();
    }, [pendingNavigation, dispatch, onHide]);

    // Abandon the draft quote and continue to the deal the user picked in Recent Deals (US2063641). The unload
    // guard is only disarmed when we actually leave, otherwise the user would be left on the page with unsaved
    // changes and no guard at all.
    const handleLeaveWithoutSaving = useCallback(() => {
        onHide();
        if (pendingNavigation) {
            onLeaveWithoutSaving();
            dispatch(baseDealActionCreators.clearPendingNavigation());
            window.location.assign(pendingNavigation);
        }
    }, [pendingNavigation, dispatch, onHide, onLeaveWithoutSaving]);

    const footer: FooterActionsType = {
        options: [
            {
                action: handleCancel,
                label: 'Cancel',
                buttonStyle: 'tertiary',
                disabled: inProgress
            },
            isDealXgRecordMissed
                ? {
                      action: handleLeaveWithoutSaving,
                      label: 'Leave without saving',
                      disabled: inProgress,
                      customIdentifiers: {
                          optionButton1: {
                              dataTestId: 'leave-without-saving-unsaved-changes'
                          }
                      }
                  }
                : {
                      action: handleSaveDraft,
                      label: draftSaveInProgress ? 'Saving...' : 'Save Draft',
                      disabled: inProgress || isLocked,
                      customIdentifiers: {
                          optionButton1: {
                              dataTestId: 'save-draft-unsaved-changes'
                          }
                      }
                  }
        ],
        primary: {
            action: handleUpdate,
            label: offerSaveInProgress ? 'Updating...' : 'Update',
            disabled: inProgress || isLocked,
            customIdentifiers: {
                primaryButton: {
                    dataTestId: 'update-unsaved-changes'
                }
            }
        }
    };

    return (
        <>
            <Modal
                data-testid="unsaved-changes-dialog"
                show={true}
                onHide={onHide}
                header={
                    <Typography variant="h3" color="sem.color.on-surface.default">
                        Unsaved Changes
                    </Typography>
                }
                footer={footer}
                size="large"
            >
                {!(draftSaveFailed || offerSaveFailed) && (
                    <Typography variant="body-md" color="sem.color.on-surface.default" tag="span">
                        You made updates to this deal that have not been saved or shared. You can save as a draft or update to
                        publish changes.
                    </Typography>
                )}
                {draftSaveFailed && (
                    <ErrorModalDialogContent
                        alertString="Failed to save draft."
                        contentString="We ran into a problem saving these changes as a draft. Please try again."
                    />
                )}
                {offerSaveFailed && (
                    <ErrorModalDialogContent
                        alertString="Offer update failed."
                        contentString="We ran into a problem updating this offer so the information wasn't shared. Please try again."
                    />
                )}
            </Modal>
        </>
    );
};

export default UnsavedChangesDialog;
